import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Camera, User } from '@/components/icons'
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar'
import { Button } from './ui/button'
import { ProfileAvatar } from './ProfileAvatar'
import { useS3GetUploadPost, uploadThumbnailBestEffort } from '@/lib/hooks/s3'
import { resizeImageFile } from '@/lib/resize-image'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

export interface ProfilePhotoPickerHandle {
  // Upload the staged photo (or apply a staged removal) against the given
  // entity id. The id is passed in (not closed over) so create mode can flush
  // against the id returned by the create mutation.
  flushPending: (entityId: string) => Promise<void>
  hasPendingChange: () => boolean
}

interface ProfilePhotoPickerProps {
  // S3 key already persisted for this entity (edit mode).
  currentS3Key?: string | null
  initials: string
  avatarColor?: string | null
  generateS3Key: (entityId: string, fileName: string) => string
  // mutateAsync of the entity's setPhoto mutation.
  setPhoto: (input: { id: string; s3Key: string | null }) => Promise<unknown>
  // Lets the parent form enable its save button on photo-only changes.
  onPendingChange?: (hasPending: boolean) => void
}

/**
 * Single profile-photo picker for the Add/Edit drawers: stages a local file
 * (object-URL preview) and uploads it on save via flushPending, so it works
 * in create mode where the entity id only exists after the create mutation.
 */
export const ProfilePhotoPicker = forwardRef<ProfilePhotoPickerHandle, ProfilePhotoPickerProps>(
  function ProfilePhotoPicker(
    { currentS3Key, initials, avatarColor, generateS3Key, setPhoto, onPendingChange },
    ref
  ) {
    const getUploadPost = useS3GetUploadPost()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pendingFile, setPendingFile] = useState<File | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [pendingRemoval, setPendingRemoval] = useState(false)

    useEffect(() => {
      onPendingChange?.(!!pendingFile || pendingRemoval)
    }, [pendingFile, pendingRemoval, onPendingChange])

    useEffect(() => {
      return () => {
        setPreviewUrl((url) => {
          if (url) URL.revokeObjectURL(url)
          return url
        })
      }
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        hasPendingChange: () => !!pendingFile || pendingRemoval,
        flushPending: async (entityId) => {
          if (pendingFile) {
            const file = await resizeImageFile(pendingFile)
            const s3Key = generateS3Key(entityId, file.name)
            const { url, fields } = (await getUploadPost.mutateAsync({
              s3Key,
              contentType: file.type || 'application/octet-stream',
            } as never)) as { url: string; fields: Record<string, string> }

            const formData = new FormData()
            Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
            formData.append('file', file)

            const res = await fetch(url, { method: 'POST', body: formData })
            if (!res.ok) throw new Error(`Upload failed (${res.status})`)

            // 400px avatar variant next to the (already 1024px) original.
            await uploadThumbnailBestEffort(file, s3Key, getUploadPost.mutateAsync as never)

            await setPhoto({ id: entityId, s3Key })
            setPendingFile(null)
            // Keep previewUrl: the local blob keeps rendering instead of
            // falling through to ProfileAvatar, which would fetch a presigned
            // URL for an image we already have in memory. The unmount effect
            // revokes it when the drawer closes.
          } else if (pendingRemoval) {
            await setPhoto({ id: entityId, s3Key: null })
            setPendingRemoval(false)
          }
        },
      }),
      [pendingFile, pendingRemoval, generateS3Key, setPhoto, getUploadPost]
    )

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error('Image exceeds the 20 MB limit')
        return
      }
      setPreviewUrl((url) => {
        if (url) URL.revokeObjectURL(url)
        return URL.createObjectURL(file)
      })
      setPendingFile(file)
      setPendingRemoval(false)
    }

    const handleRemove = () => {
      // previewUrl can outlive pendingFile (kept after a flush), so always
      // clear it; stage a removal only when a persisted photo exists.
      const hadPendingFile = !!pendingFile
      setPendingFile(null)
      setPreviewUrl((url) => {
        if (url) URL.revokeObjectURL(url)
        return null
      })
      if (!hadPendingFile && currentS3Key) setPendingRemoval(true)
    }

    const showCurrentPhoto = !!currentS3Key && !pendingRemoval
    const hasPhoto = !!previewUrl || showCurrentPhoto

    // getInitials falls back to '??' when both names are empty — show a
    // person icon instead of the placeholder text.
    const fallbackContent =
      initials && initials !== '??' ? initials : <User className="size-9" />

    return (
      <div className="flex flex-col items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelected}
        />
        <div className="relative">
          {previewUrl ? (
            <Avatar className="size-18">
              <AvatarImage src={previewUrl} className="object-cover" />
              <AvatarFallback
                className="text-[35px] font-semibold uppercase text-background"
                style={avatarColor ? { backgroundColor: avatarColor } : undefined}
              >
                {fallbackContent}
              </AvatarFallback>
            </Avatar>
          ) : (
            <ProfileAvatar
              className="size-18"
              photoS3Key={showCurrentPhoto ? currentS3Key : null}
              avatarColor={avatarColor}
              initials={fallbackContent}
              fallbackClassName="text-[35px] font-semibold uppercase text-background"
            />
          )}
          {/* Camera badge is the only way to open the picker — the avatar
              itself stays non-interactive. */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            aria-label={hasPhoto ? 'Change photo' : 'Add photo'}
            className="absolute -bottom-0.5 -right-0.5 flex size-7 items-center justify-center rounded-full border-2 border-surface-L0 bg-surface-L2 text-content-brand-bold"
          >
            <Camera className="size-4" />
          </button>
        </div>
        {hasPhoto && (
          <Button type="button" variant="destructive" size="sm" onClick={handleRemove}>
            Remove
          </Button>
        )}
      </div>
    )
  }
)
